"use client"

import { useState, useMemo } from "react"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Input } from "@/components/ui/input"
import { Search } from "lucide-react"

const faqItems = [
  {
    question: "How do I place a bulk flower order?",
    answer: "Buyers can place procurement requests directly through the Floriq platform. Simply log in, navigate to the procurement section, specify your requirements (flower type, quantity, delivery date), and our growers will respond with quotes.",
  },
  {
    question: "How does procurement coordination work?",
    answer: "Procurement teams coordinate orders and sourcing based on buyer requirements. Once you submit a request, available growers can view it and place their bids. You can review multiple options and choose the best fit for your needs.",
  },
  {
    question: "How are orders tracked?",
    answer: "Buyers can track procurement progress and order updates in real time through the app. You'll receive notifications at every stage: order confirmation, processing, packaging, and delivery.",
  },
  {
    question: "Who can use Floriq?",
    answer: "Floriq is designed for wholesalers, florists, event planners, retailers, and bulk flower buyers. Whether you're a small florist shop or a large event management company, Floriq has the right tools for your business.",
  },
  {
    question: "How do I contact support?",
    answer: "Users can contact support through WhatsApp, email, or Instagram. Our support team is available to assist you with any questions or issues you may encounter.",
  },
]

export function SupportFaq() {
  const [searchQuery, setSearchQuery] = useState("")

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return faqItems

    const query = searchQuery.toLowerCase()
    return faqItems.filter(
      (item) =>
        item.question.toLowerCase().includes(query) ||
        item.answer.toLowerCase().includes(query)
    )
  }, [searchQuery])

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Heading */}
        <div className="max-w-3xl mx-auto mb-12 md:mb-16">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground text-balance">
            How can we help?
          </h1>
        </div>

        {/* Search Bar */}
        <div className="max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 py-2.5 text-base border-border focus-visible:ring-1"
            />
          </div>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-3xl mx-auto">
          {filteredItems.length > 0 ? (
            <Accordion type="single" collapsible className="w-full">
              {filteredItems.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-base md:text-lg font-semibold hover:text-primary transition-colors">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-base">
                No results found for "{searchQuery}". Try a different search term.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
